// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------


// React
import React, { forwardRef, useState, useEffect, useRef } from "react";

// Material UI
import {
  Box,
  Button,
  Slide,
  Stack,
  Typography,
} from "@mui/material";

// Icons
import FlipIcon             from '@mui/icons-material/Flip';
import SettingsVoiceIcon    from '@mui/icons-material/SettingsVoice';
import SwapHorizIcon        from '@mui/icons-material/SwapHoriz';
import TuneIcon             from '@mui/icons-material/Tune';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import VisibilityIcon       from '@mui/icons-material/Visibility';

// Custom Components
import AligningBox from "../utils/AligningBox";
import DeviceList from "../mediaDevices/DeviceList";
import LoadingIcon from "../loading/LoadingIcon";
import RenderIf from "../utils/RenderIf";
import ScrollCard from "../ScrollCard";
import SelectionWithActionButton from "../utils/SelectionWithActionButton.js";

// Custom Scripts
import { useMediaDevices } from '../../scripts/video/devices.js';
import { WebcamState } from "../../scripts/video/webcam";
import { useScreenIsShort } from "../../scripts/utils.js";



// ----------------------------------------------------------------------------
//   Helper Components
// ----------------------------------------------------------------------------


function SettingsCardSection({ title, titleIcon, actionIcon, onClick, children }) {

  // Check the height of the screen, for responsive layout
  const screenIsShort = useScreenIsShort();

  return (
    <Box position="relative">

      {/* Icon & Title */}
      <Stack
        direction  = "row"
        position   = "absolute"
        alignItems = "center"
        top={0} left={0}
        pl={1} pr={2} py="4px" gap={1}
        sx={{
          color: "white",
          bgcolor: "#007FA4",
          borderRadius: "8px",
        }}
      >
        { React.createElement( titleIcon, {sx: { fontSize: screenIsShort ? "16px" : "18px", marginLeft: "1px" }} ) }
        <Typography component="h4" variant="p" fontSize={screenIsShort ? "0.75em" : ''}>
          { title }
        </Typography>
      </Stack>

      {/* Selection & Action Button */}
      <Box paddingLeft={2} marginTop={screenIsShort ? '16px' : '18px'} fontSize={screenIsShort ? '0.85em' : '1em'}>
        <SelectionWithActionButton
          icon     = { actionIcon }
          onClick  = { onClick }
        >
          { children }
        </SelectionWithActionButton>
      </Box>
    </Box>
  )
}



// ----------------------------------------------------------------------------
//   Helper Components: Settings Sub-Menu Pages
// ----------------------------------------------------------------------------


/**
 * Main menu page.
 */
const WebcamSettingsMainPageContent = forwardRef(({ webcam, updateSubMenu }, ref) => {

  // Track media devices
  const [ devices ] = useMediaDevices();

  // Get the device of the given kind by ID
  const getDeviceById = (kind, deviceID) => {
    return (devices.value?.[kind] || []).filter(device => device.deviceId === deviceID)[0]
  }

  // Layout
  return (
    <Stack ref={ref} gap={3} paddingY={1} paddingBottom={3} paddingRight={1}>

      {/* Choose Camera */}
      <SettingsCardSection
        title      = "Camera"
        titleIcon  = { VideoCameraFrontIcon }
        actionIcon = { <TuneIcon /> }
        onClick    = { () => updateSubMenu('video') }
      >
        { getDeviceById("video", webcam.getVideoDevice())?.label }
      </SettingsCardSection>

      {/* Choose Microphone */}
      {/*
      <SettingsCardSection
        title      = "Microphone"
        titleIcon  = { SettingsVoiceIcon }
        actionIcon = { <TuneIcon /> }
        onClick    = { () => updateSubMenu('audio') }
      >
        { getDeviceById("audio", webcam.getAudioDevice())?.label }
      </SettingsCardSection>
      */}

      {/* Camera Orientation */}
      <SettingsCardSection
        title      = "Orientation"
        titleIcon  = { FlipIcon }
        actionIcon = { <SwapHorizIcon /> }
        onClick    = { () => webcam.toggleFlipped() }
      >
        { webcam.isFlipped() ? "Mirrored" : "Normal" }
      </SettingsCardSection>

      {/* Camera Feed Visibility */}
      <SettingsCardSection
        title      = "Visibility"
        titleIcon  = { VisibilityIcon }
        actionIcon = { <SwapHorizIcon /> }
        onClick    = { () => webcam.toggleBlurred() }
      >
        { webcam.isBlurred() ? "Blurred" : "Normal" }
      </SettingsCardSection>

    </Stack>
  )
})


/**
 * Sub-menu page for a specific device type.
 */
const WebcamSettingsSubPageContent = forwardRef(({ webcam, subMenu = null, updateSubMenu }, ref) => {

  // Check the height of the screen, for responsive layout
  const screenIsShort = useScreenIsShort();

  // Track media devices
  const [ devices ] = useMediaDevices();

  // Map the sub menu page identifier to the relevant content
  const subMenuContentMap = {

    // Video Page
    "video": {
      "deviceType": "Camera",
      "deviceListProps": {
        onSelect: webcam.setVideoDevice,
        selected: webcam.getVideoDevice(),
        icon: (
          <VideoCameraFrontIcon fontSize='small' sx={{ marginLeft: "1px" }} />
        )
      },
    },

    // Audio Page
    "audio": {
      "deviceType": "Microphone",
      "deviceListProps": {
        onSelect: webcam.setAudioDevice,
        selected: webcam.getAudioDevice(),
        icon: (
          <SettingsVoiceIcon fontSize='small' />
        )
      },
    },
  }

  // Get the appropriate content set, aborting if none selected
  const content = subMenuContentMap[subMenu]
  if (subMenu === null || content === null) return null;

  // Layout
  return (
    <Stack ref={ref}
      direction="column"
      gap={2} paddingY={2}
      boxSizing="border-box"
      height="100%"
    >
      <Box flexGrow={1} minHeight={0}>
        <DeviceList
          devices    = { devices.value?.[subMenu] }
          showIDs    = { false }
          border     = "1px solid lightgray"
          listStyles = {{
            pt: 0,
            pb: 0,
            maxHeight:    "100%",
            borderRadius: "4px",
            overflowY:    "scroll",
            boxShadow:    "inset 0 0 2px lightgray",
          }}
          textStyles = {{
            fontSize:   screenIsShort ? "0.75rem !important" : '',
            fontFamily: "monospace",
          }}
          { ...content.deviceListProps }
        />
      </Box>
      <Button
        fullWidth = { true }
        onClick   = {() => { updateSubMenu(null) }}
        sx = {{
          backgroundColor: "#4447ad",
          color: "white",
        }}
      >
        Use This {content.deviceType}
      </Button>
    </Stack>
  );
})



// ----------------------------------------------------------------------------
//   Main Component(s)
// ----------------------------------------------------------------------------


/**
 * The content of the webcam settings card.
 */
export function WebcamSettingsContent({ webcam, containerRef }) {

  // Track the current menu page
  const [ subMenu, setSubMenu ] = useState(null);

  // Check webcam state
  if (webcam.stateIs( WebcamState.NONE, WebcamState.LOADING, WebcamState.FAILURE )) {
    return (
      <AligningBox top="0" left="0">
        <RenderIf condition={!webcam.stateIs( WebcamState.LOADING )} fallback={<LoadingIcon />}>
          <Box padding={3} textAlign="center">
            <Typography>Please start your camera to see the settings.</Typography>
          </Box>
        </RenderIf>
      </AligningBox>
    )
  }

  return (
    <>

      {/* Main content -- all settings */}
      <Slide in={subMenu === null} container={containerRef.current} direction="right" mountOnEnter unmountOnExit>
        <WebcamSettingsMainPageContent webcam={webcam} updateSubMenu={setSubMenu} />
      </Slide>

      {/* Slide-in sub menu -- choose device */}
      <Slide in={subMenu !== null} container={containerRef.current} direction="left" mountOnEnter unmountOnExit>
        <AligningBox top="0" left="0">
          <WebcamSettingsSubPageContent webcam={webcam} subMenu={subMenu} updateSubMenu={setSubMenu} />
        </AligningBox>
      </Slide>
    </>
  )
}


/**
 * A scrolling card containing the webcam settings content.
 */
export function WebcamSettingsCard({ webcam }) {

  // Track the parent container for sliding effect
  const containerRef = useRef(null);

  // Card layout
  return (
    <ScrollCard title="Webcam Settings" contentRef={containerRef}>
      <WebcamSettingsContent webcam={webcam} containerRef={containerRef} />
    </ScrollCard>
  )
}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------

export default { WebcamSettingsContent, WebcamSettingsCard };
